import { useCallback, useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { topmostModal, useDialogFocus } from '@/hooks/useDialogFocus';
import { useUpdateHold } from '@/hooks/useUpdateHold';
import { mapAuthError } from '@/lib/auth-errors';
import {
  SLOW_REQUEST_MS,
  abandonEmailSend,
  changeEmailSigninAddress,
  emailSigninNow,
  getEmailSigninSnapshot,
  limitedGuidanceKey,
  noteAuthenticatedSession,
  peekResumeRecord,
  pruneResumeRecord,
  resendAvailableAt,
  startEmailSend,
  startEmailVerify,
  subscribeEmailSignin,
  takeEmailSigninOutcome,
  type EmailSigninOutcome,
} from '@/lib/email-signin-resume';
import styles from './AuthModal.module.css';

interface AuthModalProps {
  onClose: () => void;
  /**
   * Coordinator z for the `mailcard-auth` layer (A1, passed by MailCardSheet).
   * The Settings `settings-auth` caller passes nothing and keeps the CSS z
   * (200) until AB models that layer.
   */
  zIndex?: number;
}

type View = 'email' | 'code';
/**
 * Why the code step shows: a code was accepted (`sent`), the reply to a send
 * was lost on reload (`pending`, "we may have sent"), or the person chose
 * "I already have a code" after a rate limit (`earlier`). Only `sent` says a
 * code was sent.
 */
type CodeMode = 'sent' | 'pending' | 'earlier';

interface ShownError {
  key: string;
  /** The field the message describes (aria-describedby). */
  field: 'email' | 'code' | null;
  /** Mark that field aria-invalid; false when the field itself is fine. */
  invalid?: boolean;
}

function initialView(): { view: View; email: string; codeMode: CodeMode } {
  const { current } = getEmailSigninSnapshot();
  const record = peekResumeRecord();
  const modeFor = (state: string | undefined): CodeMode =>
    state === 'pending' ? 'pending' : state === 'limited' ? 'earlier' : 'sent';
  if (current) {
    if (current.kind === 'send') return { view: 'email', email: current.email, codeMode: 'sent' };
    const same = record?.email === current.email ? record : null;
    return { view: 'code', email: current.email, codeMode: modeFor(same?.state) };
  }
  if (record?.state === 'sent' || record?.state === 'pending') {
    return { view: 'code', email: record.email, codeMode: modeFor(record.state) };
  }
  if (record?.state === 'limited') return { view: 'email', email: record.email, codeMode: 'earlier' };
  return { view: 'email', email: '', codeMode: 'sent' };
}

const TABBABLE = 'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

function secondsUntil(deadline: number | null | undefined, now: number): number {
  return deadline && deadline > now ? Math.ceil((deadline - now) / 1000) : 0;
}

export default function AuthModal({ onClose, zIndex }: AuthModalProps) {
  const { t } = useTranslation();
  const { user, signInWithGoogle, signInWithEmail, verifyOtp } = useAuth();
  const ops = useSyncExternalStore(subscribeEmailSignin, getEmailSigninSnapshot, getEmailSigninSnapshot);

  const [start] = useState(initialView);
  const [view, setView] = useState<View>(start.view);
  const [codeMode, setCodeMode] = useState<CodeMode>(start.codeMode);
  const [email, setEmail] = useState(start.email);
  const [code, setCode] = useState('');
  const [error, setError] = useState<ShownError | null>(null);
  const [now, setNow] = useState(emailSigninNow);
  const [keyboardInset, setKeyboardInset] = useState(0);

  const overlayRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLInputElement>(null);
  // Guards the auto-submit effect from resubmitting the same 6-digit value.
  const attemptedCodeRef = useRef('');
  const firstViewRef = useRef(true);

  const titleId = useId();
  const descId = useId();
  const errorId = useId();

  const busy = ops.current !== null;
  const sending = ops.current?.kind === 'send' || ops.current?.kind === 'resend';
  const verifying = ops.current?.kind === 'verify';

  // App-update safety: a reload mid-step is recoverable (the resume record),
  // but still hold while the code step is open or a request is in flight.
  useUpdateHold(view === 'code' || busy, 'auth-otp');

  // Focus moves in, Tab stays inside, focus returns to the opener on close.
  // Escape stays host-owned (AuthGateScreen / AuthSection / MailCardSheet).
  useDialogFocus({ open: true, containerRef: overlayRef, initialFocusRef: fieldRef });

  // Tab cycles through this dialog's own controls. useDialogFocus wraps at
  // the first and last control; this also covers the middle, because WebKit's
  // default keyboard mode skips buttons and Tab from the only text field would
  // otherwise leave the dialog. Runs after the hook's listener and does nothing
  // when the hook already handled the key.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || event.defaultPrevented) return;
      const overlay = overlayRef.current;
      if (!overlay || topmostModal() !== overlay) return;
      const items = Array.from(overlay.querySelectorAll<HTMLElement>(TABBABLE)).filter(
        (element) => element.getClientRects().length > 0,
      );
      if (items.length === 0) return;
      const index = items.indexOf(document.activeElement as HTMLElement);
      const next = event.shiftKey
        ? index <= 0 ? items.length - 1 : index - 1
        : index === -1 || index === items.length - 1 ? 0 : index + 1;
      event.preventDefault();
      items[next].focus({ preventScroll: true });
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);

  // iOS keyboard handling: the overlay is flex-centered on the full layout
  // viewport, so when the email/OTP input triggers the iOS keyboard the
  // modal's bottom (Google OAuth button in the email step, Verify button in
  // the code step) ends up behind the keyboard. Apply the keyboard height as
  // padding-bottom on the overlay to lift the centered modal above it, and
  // apply a safe-area-inset-top padding so the flex-centered modal can't
  // climb into the Dynamic Island / notch once vertical space is compressed
  // by the keyboard. 100px threshold filters out browser toolbar diffs. Same
  // pattern as SheetShell in UpdateSheet and other overlays.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const diff = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKeyboardInset(diff > 100 ? diff : 0);
    };
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    update();
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  // Drop a stored record that no longer validates (expired or bad time).
  useEffect(() => {
    pruneResumeRecord();
  }, []);

  // A session is already present: nothing to resume (no auth call).
  useEffect(() => {
    if (user) noteAuthenticatedSession();
  }, [user]);

  const visibleStep: View | 'success' = user ? 'success' : view;

  useEffect(() => {
    if (visibleStep === 'success') {
      const timer = setTimeout(onClose, 2000);
      return () => clearTimeout(timer);
    }
  }, [visibleStep, onClose]);

  const applyOutcome = useCallback((outcome: EmailSigninOutcome) => {
    if (outcome.ok) {
      if (outcome.kind === 'send' || outcome.kind === 'resend') {
        setView('code');
        setCodeMode('sent');
        setEmail(outcome.email);
        setCode('');
        // A new code replaces the old one; allow the same digits again.
        attemptedCodeRef.current = '';
        setError(null);
      }
      return;
    }
    setEmail(outcome.email);
    if (outcome.kind === 'send' && outcome.pending) {
      // The send may have gone out: show the "we may have sent" code step so
      // a code that does arrive can be entered; Resend follows its interval.
      setView('code');
      setCodeMode('pending');
      setCode('');
      attemptedCodeRef.current = '';
      setError({ key: outcome.key ?? 'auth.errorSendGeneric', field: 'code', invalid: false });
      return;
    }
    setError({
      key: outcome.key ?? 'auth.errorSendGeneric',
      field: outcome.kind === 'verify' ? 'code' : outcome.kind === 'send' ? 'email' : null,
    });
  }, []);

  // Show each finished request's result once: right away while open, or on
  // the next open when it finished while the dialog was closed.
  useEffect(() => {
    const apply = () => {
      setNow(emailSigninNow());
      const outcome = takeEmailSigninOutcome();
      if (outcome) applyOutcome(outcome);
    };
    apply();
    return subscribeEmailSignin(apply);
  }, [applyOutcome]);

  // Move focus to the new step's field (the first focus is useDialogFocus's).
  useEffect(() => {
    if (firstViewRef.current) {
      firstViewRef.current = false;
      return;
    }
    const field = fieldRef.current;
    if (!field) return;
    field.focus();
    if (view === 'email') field.select();
  }, [view]);

  const trimmedEmail = email.trim();
  const record = peekResumeRecord();
  const recordForAddress = record && record.email === trimmedEmail ? record : null;
  const sendAt =
    view === 'code'
      ? resendAvailableAt(recordForAddress)
      : recordForAddress?.state === 'limited'
        ? recordForAddress.resendAfter
        : null;
  const sendWait = secondsUntil(sendAt, now);
  const verifyWait = secondsUntil(recordForAddress?.verifyAfter, now);
  const slow = ops.current !== null && now - ops.current.startedAt >= SLOW_REQUEST_MS;
  // Rate-limit guidance comes back from a `limited` record after reopening or
  // reload. A live "please wait" message gives way to it, so both end when the
  // stated countdown ends.
  const guidanceKey = view === 'email' && !sending ? limitedGuidanceKey(recordForAddress, now) : null;
  const liveError =
    error &&
    !(error.key === 'auth.errorSendRateLimitedWait' && recordForAddress?.state === 'limited' && guidanceKey === null)
      ? error
      : null;
  const shownError: ShownError | null = liveError ?? (guidanceKey ? { key: guidanceKey, field: 'email' } : null);
  const ticking = busy || sendWait > 0 || verifyWait > 0;

  useEffect(() => {
    if (!ticking) return;
    const timer = setInterval(() => setNow(emailSigninNow()), 1000);
    return () => clearInterval(timer);
  }, [ticking]);

  // Auto-submit the moment 6 digits are present (typed or pasted) so a user in
  // a hurry doesn't have to find the button. Ref-guarded against resubmitting
  // the same value; the explicit Continue button stays for accessibility. A
  // restored code step always starts with an empty field, so restoring never
  // submits anything.
  useEffect(() => {
    if (view !== 'code' || code.length !== 6 || busy || verifyWait > 0) return;
    if (attemptedCodeRef.current === code) return;
    attemptedCodeRef.current = code;
    startEmailVerify(trimmedEmail, code, verifyOtp);
  }, [view, code, busy, verifyWait, trimmedEmail, verifyOtp]);

  async function handleGoogle() {
    setError(null);
    const result = await signInWithGoogle();
    if (result.error) setError({ key: mapAuthError(result.error), field: null });
  }

  function handleEmailChange(value: string) {
    setEmail(value);
    setError(null);
    // Changing the address makes an in-flight send no longer the current
    // attempt; its late reply is ignored.
    if (sending) abandonEmailSend();
  }

  function handleSendCode(e: React.FormEvent) {
    e.preventDefault();
    if (!trimmedEmail || sendWait > 0) return;
    setError(null);
    startEmailSend('send', trimmedEmail, signInWithEmail);
  }

  function handleVerifyCode(e: React.FormEvent) {
    e.preventDefault();
    if (code.length !== 6 || verifyWait > 0) return;
    setError(null);
    attemptedCodeRef.current = code;
    startEmailVerify(trimmedEmail, code, verifyOtp);
  }

  function handleResend() {
    if (sendWait > 0) return;
    setError(null);
    startEmailSend(recordForAddress?.state === 'sent' ? 'resend' : 'send', trimmedEmail, signInWithEmail);
  }

  function handleChangeEmail() {
    if (verifying) return;
    changeEmailSigninAddress();
    setView('email');
    setCode('');
    setError(null);
    attemptedCodeRef.current = '';
  }

  function handleHaveCode() {
    setView('code');
    setCodeMode('earlier');
    setCode('');
    setError(null);
    attemptedCodeRef.current = '';
  }

  const errorNode = shownError ? (
    <p id={errorId} className={styles.error} role="alert">
      {t(shownError.key)}
    </p>
  ) : null;
  const describedBy = (field: 'email' | 'code') =>
    [descId, shownError && shownError.field === field ? errorId : ''].filter(Boolean).join(' ');
  const slowNode = slow ? (
    <p className={styles.status} role="status">
      {t('auth.slowRequest')}
    </p>
  ) : null;

  return (
    <div
      ref={overlayRef}
      className={styles.overlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      style={
        keyboardInset
          ? {
              // Lift the centered modal above the keyboard AND clamp the top
              // so the flex-centered modal can't drift into the Dynamic Island /
              // notch on Pro iPhones (62px island) or mid-era notches (47-50px)
              // when the keyboard compresses the available vertical space.
              zIndex,
              paddingTop: 'max(env(safe-area-inset-top, 0px), 20px)',
              paddingBottom: `${keyboardInset + 20}px`,
            }
          : zIndex !== undefined
            ? { zIndex }
            : undefined
      }
    >
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button type="button" className={styles.close} onClick={onClose} aria-label={t('auth.close')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {visibleStep === 'success' ? (
          <div className={styles.successBox}>
            <p id={titleId} className={styles.successText}>{t('auth.signedIn')}</p>
          </div>
        ) : visibleStep === 'code' ? (
          <>
            <h2 id={titleId} className={styles.title}>
              {codeMode === 'earlier' ? t('auth.codeTitleEarlier') : t('auth.codeTitle')}
            </h2>
            <p id={descId} className={styles.subtitle}>
              {codeMode === 'sent'
                ? t('auth.codeSent', { email: trimmedEmail })
                : codeMode === 'pending'
                  ? t('auth.codeMaybeSent', { email: trimmedEmail })
                  : t('auth.codeEarlier', { email: trimmedEmail })}
            </p>

            <form onSubmit={handleVerifyCode} className={styles.form}>
              <input
                ref={fieldRef}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                enterKeyHint="done"
                className={`${styles.input} ${styles.codeInput}`}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                aria-label={t('auth.codePlaceholder')}
                aria-describedby={describedBy('code')}
                aria-invalid={shownError?.field === 'code' && shownError.invalid !== false ? true : undefined}
                readOnly={sending}
                maxLength={6}
                required
              />
              {errorNode}
              <button
                type="submit"
                className={styles.submit}
                disabled={busy || code.length < 6 || verifyWait > 0}
              >
                {verifying
                  ? t('auth.verifying')
                  : verifyWait > 0
                    ? t('auth.verifyIn', { seconds: verifyWait })
                    : t('auth.verify')}
              </button>
            </form>

            {codeMode !== 'earlier' && <p className={styles.status}>{t('auth.codeHint')}</p>}
            {slowNode}

            <div className={styles.actionsRow}>
              <button
                type="button"
                className={styles.linkBtn}
                onClick={handleResend}
                disabled={busy || sendWait > 0}
              >
                {sending
                  ? t('auth.sending')
                  : sendWait > 0
                    ? t('auth.resendIn', { seconds: sendWait })
                    : t('auth.resend')}
              </button>
              <button
                type="button"
                className={styles.linkBtn}
                onClick={handleChangeEmail}
                disabled={verifying}
              >
                {t('auth.changeEmail')}
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 id={titleId} className={styles.title}>{t('auth.title')}</h2>
            <p id={descId} className={styles.subtitle}>{t('auth.subtitle')}</p>

            <form onSubmit={handleSendCode} className={styles.form}>
              <input
                ref={fieldRef}
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="send"
                className={styles.input}
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                placeholder={t('auth.emailPlaceholder')}
                aria-label={t('auth.emailPlaceholder')}
                aria-describedby={describedBy('email')}
                aria-invalid={shownError?.field === 'email' && shownError.invalid !== false ? true : undefined}
                required
              />
              {errorNode}
              <button
                type="submit"
                className={styles.submit}
                disabled={sending || !trimmedEmail || sendWait > 0}
              >
                {sending
                  ? t('auth.sending')
                  : sendWait > 0
                    ? t('auth.emailMeCodeIn', { seconds: sendWait })
                    : t('auth.emailMeCode')}
              </button>
            </form>

            {slowNode}

            {recordForAddress?.state === 'limited' && !sending && (
              <div className={styles.actionsRow}>
                <button type="button" className={styles.linkBtn} onClick={handleHaveCode}>
                  {t('auth.haveCode')}
                </button>
              </div>
            )}

            <div className={styles.orDivider}>
              <span className={styles.orLine} />
              <span className={styles.orText}>{t('auth.or')}</span>
              <span className={styles.orLine} />
            </div>

            <button type="button" className={styles.googleBtn} onClick={handleGoogle}>
              <svg className={styles.socialIcon} viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 001 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              {t('auth.continueGoogle')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
